import { useEffect, useMemo, useState } from 'react'
import Display from './components/Display'
import ParamError from './components/ParamError'
import { TranslationProvider } from './context/TranslationContext'
import { usePlace } from './hooks/usePlace'
import { isDemoRequested } from './lib/demoFlag'
import { isE2eRequested } from './lib/e2eFlag'
import type { MessageSource } from './lib/messageSource'
import { createRtdbSource } from './lib/messageSource'
import { isParamError, parseDisplayParams } from './lib/params'

export default function App() {
  const params = useMemo(() => parseDisplayParams(window.location.search), [])

  if (isParamError(params)) return <ParamError kind={params.error} />

  return (
    <TranslationProvider lang={params.lang}>
      <ConnectedDisplay placeId={params.placeId} userId={params.userId} />
    </TranslationProvider>
  )
}

function ConnectedDisplay({ placeId, userId }: { placeId: string; userId: string }) {
  const place = usePlace(placeId)
  const [scriptedSource, setScriptedSource] = useState<MessageSource | null>(null)

  // Both scripted sources are dev/test-only. `import.meta.env.MODE` is
  // statically replaced, so production folds this to null and the dynamic
  // imports below become unreachable — the bundler drops them entirely.
  const scripted =
    import.meta.env.MODE === 'production'
      ? null
      : isDemoRequested(window.location.search)
        ? 'demo'
        : isE2eRequested(window.location.search)
          ? 'e2e'
          : null

  useEffect(() => {
    if (!scripted) return
    let current = true

    const load =
      scripted === 'demo'
        ? import('./demo/demoSource').then(({ createDemoSource }) => createDemoSource())
        : import('./e2e-support/windowSource').then(({ createWindowSource }) => createWindowSource())

    void load.then((source) => {
      if (current) setScriptedSource(source)
    })

    return () => {
      current = false
    }
  }, [scripted])

  const rtdbSource = useMemo(
    () => (scripted ? null : createRtdbSource(placeId, userId)),
    [scripted, placeId, userId],
  )

  const source = scriptedSource ?? rtdbSource
  if (!source) return null

  return <Display placeId={placeId} place={place} source={source} />
}
